import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import moment from 'moment';
import { FiltersService } from '../filters.service';
import { Subscription } from 'rxjs';
import { MatDateRangeInput } from '@angular/material/datepicker';
import { SettingsService } from 'src/app/modules/account/settings/account-settings.service';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-date-range-filter',
  templateUrl: './date-range-filter.component.html',
  styleUrls: ['./date-range-filter.component.scss'],
  standalone: false
})
export class DateRangeFilterComponent implements OnInit, AfterViewInit, OnDestroy {
  private filterService = inject(FiltersService);
  private settingsService = inject(SettingsService);
  private destroyed = false;

  maxDate!: Date;
  minDate!: Date;
  DEFAULT_PERIOD_DAYS = 29;
  value!: { start: Date; end: Date };
  sub?: Subscription;
  @Input() beginName = 'from_date';
  @Input() endName = 'to_date';
  @Input() persistant = true;
  @Input() allowTodayDate = false;
  @Input() onlyCompare = false;
  @Output() filterChanged = new EventEmitter<{ onlyComapre: boolean }>();
  @ViewChild('range') range!: MatDateRangeInput<Date>;

  get persistantKey(): string {
    return `filter.${this.beginName}___${this.endName}`;
  }

  async ngOnInit(): Promise<void> {
    let defaultEndDate = this.allowTodayDate ? moment().startOf('day') : moment().subtract(1, 'day').startOf('day');
    let defaultStartDate = moment(defaultEndDate).subtract(this.DEFAULT_PERIOD_DAYS, 'days').startOf('day');

    if (this.persistant) {
      const range = sessionStorage.getItem(this.persistantKey);
      if (range) {
        const rangObj = JSON.parse(range);
        defaultStartDate = moment(rangObj[this.beginName]);
        defaultEndDate = moment(rangObj[this.endName]);
      }
    }

    const features = await lastValueFrom(this.settingsService.getSettingsAccountFeatures());
    if (this.destroyed) {
      return;
    }
    if (features.dashboardDataStartDate) {
      this.minDate = moment(features.dashboardDataStartDate).toDate();
      if (defaultStartDate.isBefore(this.minDate)) {
        defaultStartDate = moment(this.minDate);
      }
    }

    this.maxDate = this.allowTodayDate ? moment().startOf('day').toDate() : moment().subtract(1, 'day').startOf('day').toDate();
    if (this.minDate && this.minDate >= this.maxDate) {
      this.maxDate = moment(this.minDate).add(1, 'days').toDate();
    }
    this.value = { start: defaultStartDate.toDate(), end: defaultEndDate.toDate() };
    this.filterService.setDefault(this.beginName, defaultStartDate.format('YYYY-MM-DD'));
    this.filterService.setDefault(this.endName, defaultEndDate.format('YYYY-MM-DD'));

    this.sub = this.filterService.currentFilter.subscribe(p => {
      if (p[this.beginName] && p[this.endName]) {
        var isFilterChanged = false;
        var beginDate = moment(p[this.beginName]).toDate();
        var endDate = moment(p[this.endName]).toDate();
        if (beginDate < this.minDate) {
          beginDate = this.minDate;
          isFilterChanged = true;
        }
        if (endDate > this.maxDate) {
          endDate = this.maxDate;
          isFilterChanged = true;
        }
        this.value = { start: beginDate, end: endDate };

        if (isFilterChanged) {
          this.apply(this.value);
        } else {
          const dates: Record<string, string> = {};
          dates[this.beginName] = moment(beginDate).format('YYYY-MM-DD');
          dates[this.endName] = moment(endDate).format('YYYY-MM-DD');
          this.persist(dates);
        }
      }
    });
  }

  ngAfterViewInit(): void {
    this.apply(this.range?.value);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.sub?.unsubscribe();
  }

  apply(dateRange: { start: Date | null; end: Date | null } | null | undefined): void {
    if (!dateRange?.start) {
      return;
    }
    const dates: Record<string, string> = {};
    dates[this.beginName] = moment(dateRange.start).format('YYYY-MM-DD');
    dates[this.endName] = moment(dateRange.end ?? dateRange.start).format('YYYY-MM-DD');
    this.filterService.applyM(dates);
    this.persist(dates);
    this.filterChanged.emit({ onlyComapre: this.onlyCompare });
  }

  private persist(dates: any): void {
    if (this.persistant) {
      sessionStorage.setItem(this.persistantKey, JSON.stringify(dates));
    }
  }
}
